/**
 * ResultScreenGlobal.tsx - Global results comparison for the Bandwagon experiment
 * Last updated: 2025-11-17 21:10 UTC
 * Updated by: JulianPolly & Copilot
 */

import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Legend } from "recharts";
import { useSession } from "../../../SessionContext";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles, chartColors } from "../../../../components/colors";

// Colors for bars
const cinemaColor = chartColors.primary[0];    // e.g. blue/green
const streamingColor = chartColors.primary[1]; // e.g. blue

const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    margin: 0,
    minWidth: 600,
    maxWidth: 800,
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minHeight: 320,
    boxSizing: "border-box"
};

type BandwagonBarData = {
    scenario: string;
    [key: string]: number | string;
};

export const ResultScreenGlobalComponent = () => {
    const { getGroupData, userData } = useSession();
    const { t, language } = useLanguage();
    const [dataOwnGroup, setDataOwnGroup] = useState<BandwagonBarData[]>([]);
    const [globalBandwagon, setGlobalBandwagon] = useState<BandwagonBarData[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingGlobal, setLoadingGlobal] = useState(true);

    // Translation keys
    const loadingText = t('experiments', 'bandwagon', 'screens', 'resultsGlobal', 'loading')
        || (language === "de" ? "Lade globale Ergebnisse…" : "Loading global results…");
    const yourClassTitle = t('experiments', 'bandwagon', 'screens', 'resultsGlobal', 'yourClass')
        || (language === "de" ? "Deine Klasse" : "Your class");
    const allClassesTitle = t('experiments', 'bandwagon', 'screens', 'resultsGlobal', 'allClasses')
        || (language === "de" ? "Alle Klassen (gesamt)" : "All classes");
    const scenario1 = t('experiments', 'bandwagon', 'screens', 'resultsGlobal', 'scenario1')
        || (language === "de" ? "Kino" : "Cinema");
    const scenario2 = t('experiments', 'bandwagon', 'screens', 'resultsGlobal', 'scenario2')
        || (language === "de" ? "Streaming" : "Streaming");
    const bed1 = t('experiments', 'bandwagon', 'screens', 'resultsGlobal', 'bedingung1')
        || (language === "de" ? "Mit sozialem Einfluss" : "With Social Influence");
    const bed2 = t('experiments', 'bandwagon', 'screens', 'resultsGlobal', 'bedingung2')
        || (language === "de" ? "Ohne sozialen Einfluss" : "Without Social Influence");
    const yAxisLabel = t('experiments', 'bandwagon', 'screens', 'resultsGlobal', 'yAxisLabel')
        || (language === "de" ? "Anzahl der Entscheidungen" : "Number of choices");

    // Helper to normalize group names
    const eigeneGruppe = userData?.gruppe ?? "";
    const normalizeGroup = (name: string | undefined | null) =>
        (name ?? "").trim().toLowerCase();

    // Build bar data out of our two-key (string) aggregation result
    const toBarChartData = (agg: Record<"0"|"1", Record<"0"|"1", number>>) => {
        // Inner keys: "0": cinema, "1": streaming
        // Outer keys: "0": Bedingung 1, "1": Bedingung 2 (match mapping above)
        return [
            {
                scenario: scenario1,
                [bed1]: agg?.["0"]?.["0"] ?? 0,
                [bed2]: agg?.["1"]?.["0"] ?? 0,
            },
            {
                scenario: scenario2,
                [bed1]: agg?.["0"]?.["1"] ?? 0,
                [bed2]: agg?.["1"]?.["1"] ?? 0,
            }
        ];
    };

    // Custom Tooltip with colored value and percentage
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const total = payload.reduce((sum: number, p: any) => sum + (p.value || 0), 0);
            return (
                <div style={{
                    background: "#fff",
                    border: `1px solid ${colors.silver}`,
                    padding: 12,
                    borderRadius: 8,
                    fontSize: 14,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                }}>
                    <div style={{
                        fontWeight: 600,
                        marginBottom: 6,
                        color: colors.brillantBlue
                    }}>
                        {label}
                    </div>
                    {payload.map((p: any) => {
                        const perc = total > 0 ? Math.round((p.value / total) * 100) : 0;
                        return (
                            <div key={p.dataKey} style={{ color: p.color || colors.textMedium }}>
                                {p.name}: {p.value} ({perc}%)
                            </div>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    // Own group data (local class)
    useEffect(() => {
        setLoading(true);
        getGroupData().then(allData => {
            // Build same structure as aggregate: { "0": {"0":count, "1":count}, "1": {...} }
            const aggClass: Record<"0"|"1", Record<"0"|"1", number>> = { "0": { "0": 0, "1": 0 }, "1": { "0": 0, "1": 0 } };
            const userGroupNorm = normalizeGroup(eigeneGruppe);

            allData?.forEach((dat: any) => {
                const gruppe = dat.gruppe;
                const gruppeNorm = normalizeGroup(gruppe);
                if (gruppeNorm !== userGroupNorm) return;
                const bedingung = dat?.randomizer3 === "Bedingung 2" ? "1" : "0";
                const choice = dat?.BandwagonEffect?.["0"]?.selections?.[0]?.choice;
                if (choice === "cinema") aggClass[bedingung]["0"] += 1;
                if (choice === "streaming") aggClass[bedingung]["1"] += 1;
            });
            setDataOwnGroup(toBarChartData(aggClass));
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [getGroupData, eigeneGruppe, scenario1, scenario2, bed1, bed2]);

    // Global data
    useEffect(() => {
        setLoadingGlobal(true);
        fetch("/api/aggregateGlobalData")
            .then(res => res.json())
            .then(apiJson => {
                const agg = apiJson?.data?.BandwagonEffect;
                setGlobalBandwagon(toBarChartData(agg ?? { "0": { "0": 0, "1": 0 }, "1": { "0": 0, "1": 0 } }));
                setLoadingGlobal(false);
            }).catch(() => setLoadingGlobal(false));
    }, [scenario1, scenario2, bed1, bed2]);

    // Bar chart settings
    const barSize = 34;

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {t('experiments', 'bandwagon', 'screens', 'resultsGlobal', 'title') ||
                    (language === "de" ? "Bandwagon-Effekt – Globale Ergebnisse" : "Bandwagon Effect – Global Results")}
            </h2>
            <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 48,
                marginTop: 24,
                width: "100%"
            }}>
                {/* Class Results */}
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 10
                    }}>
                        {yourClassTitle}
                    </h3>
                    <div style={{ flex: 1, width: "100%" }}>
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart
                                data={dataOwnGroup}
                                margin={{ top: 32, right: 16, left: 16, bottom: 28 }}
                                layout="vertical"
                                barCategoryGap="25%"
                            >
                                <XAxis type="number" allowDecimals={false} label={{ value: yAxisLabel, position: "insideTopLeft", offset: 0 }}/>
                                <YAxis dataKey="scenario" type="category" width={160} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar dataKey={bed1} fill={cinemaColor} name={bed1} barSize={barSize}>
                                    <LabelList dataKey={bed1} position="right" fontSize={15} />
                                </Bar>
                                <Bar dataKey={bed2} fill={streamingColor} name={bed2} barSize={barSize}>
                                    <LabelList dataKey={bed2} position="right" fontSize={15} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                {/* Global */}
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 10
                    }}>
                        {allClassesTitle}
                    </h3>
                    <div style={{ flex: 1, width: "100%" }}>
                        {loadingGlobal ? (
                            <div style={{ color: colors.brillantBlue, fontSize: 17, textAlign: "center", marginTop: 40 }}>
                                {loadingText}
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart
                                    data={globalBandwagon}
                                    margin={{ top: 32, right: 16, left: 16, bottom: 28 }}
                                    layout="vertical"
                                    barCategoryGap="25%"
                                >
                                    <XAxis type="number" allowDecimals={false} label={{ value: yAxisLabel, position: "insideTopLeft", offset: 0 }}/>
                                    <YAxis dataKey="scenario" type="category" width={160} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar dataKey={bed1} fill={cinemaColor} name={bed1} barSize={barSize}>
                                        <LabelList dataKey={bed1} position="right" fontSize={15} />
                                    </Bar>
                                    <Bar dataKey={bed2} fill={streamingColor} name={bed2} barSize={barSize}>
                                        <LabelList dataKey={bed2} position="right" fontSize={15} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ResultScreenGlobal: ScreenConfig = {
    component: ResultScreenGlobalComponent,
    defaultResult: {},
    canContinueDefault: true,
};